import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { MapItem, TemplateMapVariant } from '../types';
import { getTodayDate } from '../utils';
import { TransportItemCard } from './TransportItemCard';
import { FreightItemCard } from './FreightItemCard';
import styles from '../styles.module.scss';

interface MapSidebarProps {
  isOpen: boolean;
  variant: TemplateMapVariant;
  items: MapItem[];
  count: number;
  selectedItemId: string | null;
  loadingRadius: number;
  unloadingRadius: number;
  dateFrom: string;
  dateTo: string;
  isRefetching: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  templateId?: string;
  searchRoute: string;
  itemIdParam: string;
  translationKeys: {
    loadingRadius: string;
    unloadingRadius: string;
    dateFrom: string;
    dateTo: string;
    itemsFound: string;
  };
  onLoadingRadiusChange: (value: number) => void;
  onUnloadingRadiusChange: (value: number) => void;
  onDateFromChange: (value: string) => void;
  onDateToChange: (value: string) => void;
  onApplyFilters: () => void;
  onItemClick: (item: MapItem) => void;
  onLoadMore: () => void;
}

export const MapSidebar: React.FC<MapSidebarProps> = ({
  isOpen,
  variant,
  items,
  count,
  selectedItemId,
  loadingRadius,
  unloadingRadius,
  dateFrom,
  dateTo,
  isRefetching,
  isLoadingMore,
  templateId,
  searchRoute,
  itemIdParam,
  translationKeys,
  onLoadingRadiusChange,
  onUnloadingRadiusChange,
  onDateFromChange,
  onDateToChange,
  onApplyFilters,
  onItemClick,
  onLoadMore,
}) => {
  const { t } = useTranslation();
  const today = getTodayDate();
  const sentinelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Setup IntersectionObserver once
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const list = listRef.current;
    if (!sentinel || !list) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          onLoadMoreRef.current();
        }
      },
      {
        root: list,
        rootMargin: '500px',
        threshold: 0,
      }
    );

    observerRef.current.observe(sentinel);
    return () => observerRef.current?.disconnect();
  }, []);

  // Re-observe sentinel after loading completes to trigger a fresh intersection check
  const wasLoadingMoreRef = useRef(false);
  useEffect(() => {
    if (isLoadingMore) {
      wasLoadingMoreRef.current = true;
    } else if (wasLoadingMoreRef.current) {
      wasLoadingMoreRef.current = false;
      const sentinel = sentinelRef.current;
      if (sentinel && observerRef.current) {
        requestAnimationFrame(() => {
          observerRef.current?.unobserve(sentinel);
          observerRef.current?.observe(sentinel);
        });
      }
    }
  }, [isLoadingMore]);

  const handleRadiusChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: (value: number) => void
  ) => {
    const val = e.target.value.replace(/\D/g, '');
    const num = parseInt(val) || 0;
    setter(Math.min(num, 999));
  };

  return (
    <div className={`${styles.sidebar} ${isOpen ? styles.sidebarOpen : ''}`}>
      <div className={styles.filters}>
        <div className={styles.filterGroup}>
          <label>{t(translationKeys.loadingRadius, 'Радиус загрузки')}</label>
          <div className={styles.radiusInput}>
            <input
              type="text"
              inputMode="numeric"
              placeholder="0"
              value={loadingRadius || ''}
              onChange={(e) => handleRadiusChange(e, onLoadingRadiusChange)}
            />
            <span className={styles.radiusSuffix}>{t('main.search.distance', 'км')}</span>
          </div>
        </div>
        <div className={styles.filterGroup}>
          <label>{t(translationKeys.unloadingRadius, 'Радиус выгрузки')}</label>
          <div className={styles.radiusInput}>
            <input
              type="text"
              inputMode="numeric"
              placeholder="0"
              value={unloadingRadius || ''}
              onChange={(e) => handleRadiusChange(e, onUnloadingRadiusChange)}
            />
            <span className={styles.radiusSuffix}>{t('main.search.distance', 'км')}</span>
          </div>
        </div>
        <div className={styles.filterRow}>
          <div className={styles.filterGroup}>
            <label>{t(translationKeys.dateFrom, 'Дата с')}</label>
            <input
              type="date"
              className={styles.dateInput}
              value={dateFrom}
              min={today}
              max={dateTo || undefined}
              onChange={(e) => onDateFromChange(e.target.value)}
            />
          </div>
          <div className={styles.filterGroup}>
            <label>{t(translationKeys.dateTo, 'Дата по')}</label>
            <input
              type="date"
              className={styles.dateInput}
              value={dateTo}
              min={dateFrom || today}
              onChange={(e) => onDateToChange(e.target.value)}
            />
          </div>
        </div>
        <button
          className={styles.applyButton}
          onClick={onApplyFilters}
          disabled={isRefetching}
        >
          {isRefetching ? t('common.loading', 'Загрузка...') : t('common.apply', 'Применить')}
        </button>
      </div>

      <div className={styles.freightListHeader}>
        {t(translationKeys.itemsFound, { count, defaultValue: `Найдено: ${count}` })}
      </div>
      <div className={styles.freightList} ref={listRef}>
        {items.map((item) => (
          variant === 'transport' ? (
            <TransportItemCard
              key={item.id}
              item={item}
              isSelected={selectedItemId === item.id}
              templateId={templateId}
              searchRoute={searchRoute}
              itemIdParam={itemIdParam}
              onClick={() => onItemClick(item)}
            />
          ) : (
            <FreightItemCard
              key={item.id}
              item={item}
              isSelected={selectedItemId === item.id}
              templateId={templateId}
              searchRoute={searchRoute}
              itemIdParam={itemIdParam}
              onClick={() => onItemClick(item)}
            />
          )
        ))}
        {/* Sentinel element for infinite scroll */}
        <div ref={sentinelRef} className={styles.scrollSentinel}>
          {isLoadingMore && (
            <div className={styles.loadingIndicator}>
              {t('common.loading', 'Загрузка...')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
